import React, { useState, useRef } from "react";
import { useHistory, useParams } from "react-router";
import { useFormik, FormikProvider, FieldArray } from "formik";
import SignatureCanvas from "react-signature-canvas";
import moment from "moment";

// utils
import {
  createAssignmentList,
  calculateTotalManHours,
} from "./ProcessTimeTickets.utils";
import { validate } from "../../../shared/utility";

// graphql
import { useMutation } from "@apollo/client";
import { PROCESS_JOB_DAY_TIME_TICKETS } from "../../../graphql/jobDay/jobDay.mutations";
import { apolloClient } from "../../../index";
import { GET_JOB_DAY_BY_ID_FOR_PROCESS_TIME_TICKETS } from "../../../graphql/jobDay/jobDay.queries";

// styles
import "./ProcessTimeTickets.css";

// components
import InputField from "../../../components/forms/InputField/InputField.component";
import ActionButton from "../../../components/buttons/ActionButton/ActionButton.component";
import ProcessTimeTicketInput from "../../../components/forms/lists/ProcessTimeTicketInput/ProcessTimeTicketInput.component";

// mui
import {
  FormControlLabel,
  Checkbox,
  TextField,
  MenuItem,
  Grid,
  Container,
  Typography,
} from "@mui/material";

// collections
import JobInfo from "./collections/JobInfo/JobInfo.collection";
import SelectField from "../../../components/forms/SelectField/SelectField.component";

const workTypeChoices = [
  { value: "TM", label: "T & M" },
  { value: "CW", label: "Contract Work" },
  { value: "YW", label: "Yard Work" },
];

const workType2Choices = [
  { value: "IN", label: "Interior" },
  { value: "EX", label: "Exterior" },
];

const workType1Choices = [
  { value: "E", label: "Erect" },
  { value: "D", label: "Dismantle" },
];

const validationSchema = {
  scopeOfWork: {
    type: "input",
    validation: {
      required: true,
    },
  },
  safetyNotes: {
    type: "input",
    validation: {
      required: true,
    },
  },
  numFrames: {
    type: "input",
    validation: {
      required: true,
      isNumeric: true,
    },
  },
  workType: {
    type: "input",
    validation: {
      required: true,
    },
  },
  workType1: {
    type: "input",
    validation: {
      required: true,
    },
  },
  workType2: {
    type: "input",
    validation: {
      required: true,
    },
  },
  assignments: {
    type: "list",
    fields: {
      startTime: {
        type: "time",
        validation: {
          required: true,
          isTime: true,
        },
      },
      endTime: {
        type: "time",
        validation: {
          required: true,
          isTime: true,
        },
      },
      safetyAcknowledgment: {
        type: "boolean",
        validation: {
          required: true,
        },
      },
    },
  },
};

export default function ProcessTimeTickets() {
  const history = useHistory();
  const { jobDayId } = useParams();
  const parsedJobDayId = parseInt(jobDayId, 10);
  const customerSignature = useRef();
  const assignmentSignatures = useRef([]);
  const [processTimeTicketsMutation] = useMutation(
    PROCESS_JOB_DAY_TIME_TICKETS
  );
  const [isFetchingObject, setIsFetchingObject] = useState(true);
  const [formErrors, setFormErrors] = useState({ assignments: [] });

  const formik = useFormik({
    initialValues: {
      scopeOfWork: "",
      safetyNotes: "",
      numFrames: "",
      workType: "OO",
      workType1: "E",
      workType2: "IN",
      assignments: [],
      customerSignature: null,
      customerComments: "",
    },
  });

  const clearCustomerSignature = () => {
    if (formik.values.customerSignature) {
      formik.setFieldValue("customerSignature", null);
    } else if (customerSignature.current) {
      customerSignature.current.clear();
    }
  };

  const clearAssignmentSignature = (index) => {
    // check if it is an image being displayed
    const signature = formik.values.assignments[index].signature;

    // if yes, show canvas
    if (signature) {
      formik.setFieldValue(`assignments.${index}.signature`, null);
    } else {
      // if no, show signature canvas
      if (assignmentSignatures.current[index]) {
        assignmentSignatures.current[index].clear();
      }
    }
  };

  if (isFetchingObject) {
    // fetch the data
    apolloClient
      .query({
        query: GET_JOB_DAY_BY_ID_FOR_PROCESS_TIME_TICKETS,
        variables: { id: parsedJobDayId }, // ✅ integer
      })
      .catch((e) => {
        console.error(e);
      })
      .then((result) => {
        if (result) {
          setIsFetchingObject(false);

          formik.setFieldValue("assignments", [
            ...result.data.jobDay.crewMemberAssignments.map((assignment) => ({
              id: assignment.id,
              crewMember: { ...assignment.crewMember },
              startTime: moment(`12/12/12 ${assignment.startTime}`).isValid()
                ? moment(`12/12/12 ${assignment.startTime}`).format("HH:mm")
                : null,
              endTime: moment(`12/12/12 ${assignment.endTime}`).isValid()
                ? moment(`12/12/12 ${assignment.endTime}`).format("HH:mm")
                : null,
              safetyAcknowledgment: assignment.safetyAcknowledgment,
              signature: assignment.signOff
                ? assignment.signOff.signature
                : null,
            })),
          ]);
          formik.setFieldValue(
            "scopeOfWork",
            result.data.jobDay.scopeOfWork ? result.data.jobDay.scopeOfWork : ""
          );
          formik.setFieldValue(
            "safetyNotes",
            result.data.jobDay.safetyNotes ? result.data.jobDay.safetyNotes : ""
          );
          formik.setFieldValue("workType", result.data.jobDay.workType);
          formik.setFieldValue("workType1", result.data.jobDay.workType1);
          formik.setFieldValue("workType2", result.data.jobDay.workType2);
          formik.setFieldValue("numFrames", result.data.jobDay.numFrames);
          formik.setFieldValue(
            "customerSignature",
            result.data.jobDay.customerSignature
              ? result.data.jobDay.customerSignature
              : null
          );
          formik.setFieldValue(
            "customerComments",
            result.data.jobDay.customerComments
          );
        }
      });
  }

  const handleSubmit = async () => {
    // validate form
    const { isValid, errors } = validate(formik.values, validationSchema);
    console.log("Validation result:", { isValid, errors }); // 👈 Add this
    console.log("Form values:", formik.values); // 👈 Add this

    if (isValid) {
      // get signatures
      let assignmentsWithSignatures = formik.values.assignments.map(
        (assignment, index) => {
          // get signature
          return {
            ...assignment,
            signature: assignment.signature
              ? assignment.signature
              : assignmentSignatures.current[index].getSignature(),
          };
        }
      );

      const submitData = {
        ...formik.values,
        id: parsedJobDayId,
        numFrames: parseInt(formik.values.numFrames, 10),
        assignments: [...createAssignmentList(assignmentsWithSignatures)],
        customerSignature: formik.values.customerSignature
          ? formik.values.customerSignature
          : customerSignature.current.getTrimmedCanvas().toDataURL("image/png"),
      };
      processTimeTicketsMutation({ variables: { ...submitData } })
        .catch((e) => {
          console.error(e);
        })
        .then((result) => {
          if (result && result.data) {
            setFormErrors({ assignments: [] });
            history.push(`/schedule/details/${parsedJobDayId}`);
          }
        });
    } else {
      console.log("Validation failed with errors:", errors); // 👈 Add this
      setFormErrors(errors);
    }
  };

  const totalHours = calculateTotalManHours(formik.values.assignments);

  return (
    <div>
      <Container disableGutters sx={{ marginBottom: 4 }}>
        <Typography variant="h2">Process Time Tickets</Typography>
      </Container>

      <JobInfo jobDayId={jobDayId} />

      <hr className="process-time-tickets-divider" />

      <Grid container spacing={2} sx={{ width: "100%", marginBottom: "24px" }}>
        <Grid item xs={6}>
          <InputField
            type="number"
            id="numFrames"
            name="numFrames"
            onChange={formik.handleChange}
            value={formik.values.numFrames}
            placeholder="# of Frames"
            required
            isValid={formErrors["numFrames"]}
          />
        </Grid>

        <Grid item xs={6}>
          <SelectField
            id="workType"
            label="Work Type"
            options={workTypeChoices}
            handleChange={(value) =>
              formik.setFieldValue("workType", value.value)
            }
            value={formik.values.workType}
            required
            isValid={formErrors["workType"]}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ width: "100%", marginBottom: "24px" }}>
        <Grid item xs={6}>
          <SelectField
            id="workType1"
            label="Erect/Dismantle"
            options={workType1Choices}
            handleChange={(value) =>
              formik.setFieldValue("workType1", value.value)
            }
            value={formik.values.workType1}
            required
            isValid={formErrors["workType1"]}
          />
        </Grid>

        <Grid item xs={6}>
          <SelectField
            id="workType2"
            label="Interior/Exterior"
            options={workType2Choices}
            handleChange={(value) =>
              formik.setFieldValue("workType2", value.value)
            }
            value={formik.values.workType2}
            required
            isValid={formErrors["workType2"]}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ width: "100%", marginBottom: "24px" }}>
        <Grid item xs={12}>
          <InputField
            type="textarea"
            id="scopeOfWork"
            name="scopeOfWork"
            onChange={formik.handleChange}
            value={formik.values.scopeOfWork}
            placeholder="Scope of Work"
            isValid={formErrors["scopeOfWork"]}
          />
        </Grid>

        <Grid item xs={12}>
          <InputField
            type="textarea"
            id="safetyNotes"
            name="safetyNotes"
            onChange={formik.handleChange}
            value={formik.values.safetyNotes}
            placeholder="Safety Notes"
            required
            isValid={formErrors["safetyNotes"]}
          />
        </Grid>
      </Grid>

      <FormikProvider value={formik}>
        <Typography variant="h4">Crew Member Sign-Off</Typography>
        <FieldArray
          name="assignments"
          render={() => (
            <React.Fragment>
              {formik.values.assignments.map((assignment, index) => {
                return (
                  <ProcessTimeTicketInput
                    key={`assignments.${index}`}
                    index={index}
                    crewMember={assignment.crewMember}
                    prefix={`assignments.${index}`}
                    startTime={assignment.startTime}
                    endTime={assignment.endTime}
                    safetyAcknowledgment={assignment.safetyAcknowledgment}
                    handleChange={formik.handleChange}
                    signatureImage={assignment.signature}
                    onClearClicked={clearAssignmentSignature}
                    ref={(element) =>
                      (assignmentSignatures.current[index] = element)
                    }
                    errors={
                      formErrors["assignments"].length > index
                        ? formErrors["assignments"][index]
                        : {}
                    }
                  />
                );
              })}
            </React.Fragment>
          )}
        />

        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h6">
              Total Hours: {!isNaN(totalHours) ? totalHours : ""}
            </Typography>
          </Grid>
        </Grid>

        <div className="process-time-tickets-signature-top">
          <Typography variant="h4">Customer Approval</Typography>
          <ActionButton
            color="error"
            label="Clear"
            size="small"
            onClick={clearCustomerSignature}
          />
        </div>

        <div className="signature-container">
          {formik.values.customerSignature ? (
            <img
              className="signature-canvas"
              src={formik.values.customerSignature}
              alt="Customer Signature"
            />
          ) : (
            <SignatureCanvas
              penColor="black"
              canvasProps={{ className: "signature-canvas" }}
              ref={customerSignature}
              clearOnResize={false}
            />
          )}
        </div>

        <Grid container sx={{ marginTop: 2 }}>
          <Grid item xs={12}>
            <InputField
              type="textarea"
              id="customerComments"
              name="customerComments"
              onChange={formik.handleChange}
              value={formik.values.customerComments}
              placeholder="Customer Comments"
              isValid={formErrors["customerComments"]}
              required={false}
            />
          </Grid>
        </Grid>
      </FormikProvider>

      <Container disableGutters sx={{ marginBottom: 4 }}>
        <Grid container spacing={2}>
          <Grid item>
            <ActionButton
              label="Cancel"
              color="secondary"
              onClick={() => history.goBack()}
            />
          </Grid>
          <Grid item>
            <ActionButton
              label="Save Time Tickets"
              color="success"
              onClick={handleSubmit}
            />
          </Grid>
        </Grid>
      </Container>
    </div>
  );
}
